import { createColorPaletteEngine } from '@yola/ws-template-color-palette-generator';
import { customization } from '@yola/ws-sdk';
import store from 'src/js/store';
import generateCustomPaletteId from './generate-custom-palette-id';
import normalizeCustomPalette from './normalize-custom-palette';
import { mapColorKeysToBaseColors, mapBaseColorKeysToSurfaceIds } from './map-palette-colors';

const clonePaletteIfNeeded = (originPalette, { forceClone = false }) => {
  if (originPalette.parentId && !forceClone) {
    return originPalette;
  }

  return {
    ...originPalette,
    id: generateCustomPaletteId(),
    parentId: originPalette.parentId || originPalette.id,
  };
};

const createPaletteEngine = (originPalette, options = {}) => {
  const state = store.getState();
  const config = customization.selectors.getColorPaletteConfig(state);

  const palette = clonePaletteIfNeeded(originPalette, options);

  const normalizedPalette = normalizeCustomPalette(palette);

  const nativeEngine = createColorPaletteEngine({
    id: palette.id,
    config,
    colors: mapColorKeysToBaseColors(normalizedPalette.colors),
    schemes: normalizedPalette.schemes,
  });

  const withId = (output) => ({ ...output, id: palette.id });

  const nativeEngineMethods = {
    exportState: () => {
      const { colors, schemes = {} } = nativeEngine.exportState();
      return {
        id: palette.id,
        parentId: palette.parentId,
        colors: mapBaseColorKeysToSurfaceIds(colors),
        // TODO: all defensive checks for the `schemes` property can be removed later,
        // after we move the default color schemes there as well
        ...(Object.keys(schemes).length && { schemes }),
      };
    },
    getOutput: () => withId(nativeEngine.getOutput()),
    updateColors: (colors) => withId(nativeEngine.updateColors(mapColorKeysToBaseColors(colors))),
    updateSchemeOverrides: (...args) => withId(nativeEngine.updateSchemeOverrides(...args)),
    resetSchemeOverrides: (...args) => withId(nativeEngine.resetSchemeOverrides(...args)),
    setCustomScheme: (...args) => withId(nativeEngine.setCustomScheme(...args)),
    deleteCustomScheme: (...args) => withId(nativeEngine.deleteCustomScheme(...args)),
  };

  return {
    ...nativeEngineMethods,
    resetColorScheme: ({ colorSchemeId, initialColors }) => {
      let output;
      const { schemes } = nativeEngineMethods.exportState();
      const overridesToReset = Object.keys(schemes?.[colorSchemeId]?.variableOverrides || {});
      if (overridesToReset.length) {
        output = nativeEngineMethods.resetSchemeOverrides(colorSchemeId, overridesToReset);
      }

      const initialBaseColor = initialColors[colorSchemeId];
      if (initialBaseColor) {
        output = nativeEngineMethods.updateColors({ [colorSchemeId]: initialBaseColor });
      }

      return output || nativeEngineMethods.getOutput();
    },
  };
};

export default createPaletteEngine;
